/**
 * Resolve path :mediaId for PATCH …/media/asset/:mediaId/save-edited.
 * Review-panel rows: use contentTopicPostMedias[].media.id (parent Media).
 * Fallback to join-row id only when media is null. Never use MediaAsset.id in the path.
 */

/** Edit-image file → save-edited path media id (parent Media.id, never MediaAsset.id). */
export function resolveParentMediaIdFromEditImageFile(file) {
  if (!file) return null
  const ad = file.apiData || {}
  const assetId = resolveMediaAssetIdFromEditImageFile(file)

  if (!isTempId(ad.mediaId)) {
    if (assetId == null || String(ad.mediaId) !== String(assetId)) {
      return ad.mediaId
    }
  }

  const candidates = [
    ad.originalResponse?.media?.id,
    ad.originalResponse?.mediaId,
    ad.originalResponse?.id,
    file.mediaId,
  ]
  for (const candidate of candidates) {
    if (isTempId(candidate)) continue
    if (assetId != null && String(candidate) === String(assetId)) continue
    return candidate
  }

  const postMediaRowId = ad.contentTopicPostMediaId
  if (!isTempId(postMediaRowId)) {
    if (assetId == null || String(postMediaRowId) !== String(assetId)) {
      return postMediaRowId
    }
  }

  return null
}

/** Path param for PATCH save-edited — parent Media.id, or asset id only when parent is unknown. */
export function resolveMediaIdFromEditImageFile(file) {
  const parentMediaId = resolveParentMediaIdFromEditImageFile(file)
  if (parentMediaId != null) return parentMediaId
  return resolveMediaAssetIdFromEditImageFile(file)
}

/** True only when save-edited has no path media id and must use ?postMediaId= (legacy topic GET). */
export function shouldIncludePostMediaIdOnSaveEdited(file) {
  const ad = file?.apiData || {}
  const postMediaId = ad.contentTopicPostMediaId
  if (postMediaId == null || String(postMediaId).trim() === "") return false

  const pathMediaId = resolveMediaIdFromEditImageFile(file)
  if (pathMediaId != null && !isTempId(pathMediaId)) return false

  return true
}

/**
 * Resolve MediaAsset.id from API rows (payloads, tags, etc.).
 * Never use contentTopicPostMedias[].id (join row) or parent Media.id.
 */

function isTempId(id) {
  return id == null || id === "" || String(id).startsWith("temp-")
}

/** Primary MediaAsset.id from one POST /media/clients upload row (never parent Media.id). */
export function resolveMediaAssetIdFromUploadMediaRow(media) {
  if (!media || typeof media !== "object") return null
  const mediaEntityId = media.id ?? media.mediaId ?? media.media_id ?? null

  const assets = media.mediaAssets
  if (Array.isArray(assets) && assets.length > 0) {
    for (const a of assets) {
      const candidate = a?.id ?? a?.mediaAssetId ?? a?.assetId ?? null
      if (candidate == null) continue
      if (mediaEntityId != null && String(candidate) === String(mediaEntityId)) continue
      return candidate
    }
  }

  const flatCandidates = [media.mediaAssetId, media.media_asset_id, media.id_asset]
  for (const candidate of flatCandidates) {
    if (candidate == null) continue
    if (mediaEntityId != null && String(candidate) === String(mediaEntityId)) continue
    return candidate
  }

  return null
}

/** Collect one MediaAsset.id per uploaded file from upload API `medias[]`. */
export function collectMediaAssetIdsFromUploadMedias(medias, fileCount = null) {
  if (!Array.isArray(medias)) return []
  const rows = fileCount != null && fileCount >= 0 ? medias.slice(0, fileCount) : medias
  const ids = []
  const seen = new Set()
  for (const media of rows) {
    const id = resolveMediaAssetIdFromUploadMediaRow(media)
    if (id == null) continue
    const key = String(id)
    if (seen.has(key)) continue
    seen.add(key)
    ids.push(id)
  }
  return ids
}

function pickAssetId(candidate, mediaEntityId) {
  if (isTempId(candidate)) return null
  if (mediaEntityId != null && String(candidate) === String(mediaEntityId)) return null
  return candidate
}

export function resolveMediaAssetIdFromContentTopicPostMedia(row) {
  if (!row || typeof row !== "object") return null
  const mediaEntityId = row.media?.id ?? null
  return pickAssetId(row.mediaAsset?.id, mediaEntityId)
}

/** save-edited PATCH response → next media asset id (never parent Media.id). */
export function resolveMediaAssetIdFromSaveEditedResponse(result, previousAssetId = null) {
  if (!result || typeof result !== "object") {
    return previousAssetId ?? null
  }

  const mediaEntityId = result.id ?? result.mediaId ?? null
  const fromArray = result.mediaAssets?.[0]?.id ?? result.mediaAsset?.id ?? null
  const nested = pickAssetId(fromArray, mediaEntityId)
  if (nested != null) return nested

  const topLevel = pickAssetId(result.id, null)
  if (
    topLevel != null &&
    !Array.isArray(result.mediaAssets) &&
    result.mediaAsset == null
  ) {
    return topLevel
  }

  return previousAssetId ?? null
}

/** Edit-image / campaign drawer file → media asset id for save-edited URL. */
export function resolveMediaAssetIdFromEditImageFile(file) {
  if (!file) return null

  const ad = file.apiData || {}
  const mediaEntityId = ad.mediaId ?? ad.originalResponse?.id ?? null

  const candidates = [
    ad.originalResponse?.mediaAssets?.[0]?.id,
    ad.originalResponse?.mediaAsset?.id,
    ad.mediaAssetId,
    ad.assetId,
    ad.originalResponse?.data?.[0]?.mediaAssets?.[0]?.id,
  ]

  for (const candidate of candidates) {
    const picked = pickAssetId(candidate, mediaEntityId)
    if (picked != null) return picked
  }

  const fromId = pickAssetId(ad.id, mediaEntityId)
  if (fromId != null) return fromId

  const fromFile = pickAssetId(file.mediaAssetId, mediaEntityId)
  if (fromFile != null) return fromFile

  return null
}

/** Asset ids that can be sent on PATCH posts/:id (skip temp / pipeline placeholders). */
export function collectPersistableMediaAssetIds(files) {
  if (!Array.isArray(files)) return []
  const ids = []
  const seen = new Set()
  for (const file of files) {
    const raw = resolveMediaAssetIdFromEditImageFile(file)
    if (raw == null || raw === "") continue
    const key = String(raw)
    if (key.startsWith("temp-") || key.startsWith("pipeline-")) continue
    if (seen.has(key)) continue
    seen.add(key)
    const n = Number(raw)
    ids.push(Number.isNaN(n) ? raw : n)
  }
  return ids
}

/** Save-edited path id from a contentTopicPostMedias row (topic fetch API). */
export function resolveParentMediaIdFromContentTopicPostMedia(row, post = null) {
  if (!row || typeof row !== "object") {
    if (post?.mediaId != null && post.mediaId !== "") return post.mediaId
    if (post?.media_id != null && post.media_id !== "") return post.media_id
    return null
  }
  const fromMedia =
    row.media?.id ??
    row.mediaId ??
    row.media_id ??
    row.media?.mediaId ??
    row.mediaAsset?.media?.id ??
    row.mediaAsset?.mediaId ??
    row.mediaAsset?.media_id ??
    null
  if (fromMedia != null && fromMedia !== "") return fromMedia

  // Legacy fallback when media relation is missing on the row.
  if (row.id != null && row.id !== "") return row.id

  return null
}

/** Parent Media.id from fetchUserMedias / AI Asset Manager library row. */
export function resolveParentMediaIdFromLibraryAsset(asset) {
  if (!asset || typeof asset !== "object") return null
  return (
    asset.id_media ??
    asset.media?.id ??
    asset.mediaId ??
    asset.media_id_x ??
    asset.media_id_y ??
    null
  )
}

/** MediaAsset.id from fetchUserMedias / AI Asset Manager library row (never parent Media.id). */
export function resolveMediaAssetIdFromLibraryAsset(asset) {
  if (!asset || typeof asset !== "object") return null
  const mediaEntityId = resolveParentMediaIdFromLibraryAsset(asset)
  const candidates = [
    asset.id_asset,
    asset.media_asset_id,
    asset.mediaAssets?.[0]?.id,
    asset.mediaAsset?.id,
  ]
  for (const candidate of candidates) {
    const picked = pickAssetId(candidate, mediaEntityId)
    if (picked != null) return picked
  }
  return null
}

/** apiData for Edit Image from a library / media API asset. */
export function buildEditImageApiDataFromLibraryAsset(asset) {
  const mediaId = resolveParentMediaIdFromLibraryAsset(asset)
  const assetId = resolveMediaAssetIdFromLibraryAsset(asset)
  return {
    id: assetId,
    assetId,
    mediaAssetId: assetId,
    mediaId,
    originalResponse: {
      id: mediaId,
      mediaAssets: assetId ? [{ id: assetId }] : [],
    },
  }
}

/** apiData for Edit Image from a topic fetch contentTopicPostMedias row. */
export function buildEditImageApiDataFromContentTopicPostMedia(row, post = null) {
  const assetId = resolveMediaAssetIdFromContentTopicPostMedia(row)
  const mediaId = resolveParentMediaIdFromContentTopicPostMedia(row, post)
  const contentTopicPostMediaId = row?.id ?? null
  const assetUrl = row?.mediaAsset?.assetUrl || null
  return {
    id: assetId,
    assetId,
    mediaAssetId: assetId,
    contentTopicPostMediaId,
    mediaId,
    serverUrl: assetUrl,
    originalResponse: {
      id: mediaId,
      media: row?.media ?? (mediaId != null ? { id: mediaId } : null),
      mediaAssets: assetId && assetUrl ? [{ id: assetId, assetUrl }] : [],
    },
  }
}
